/**
 * Web purchase flow: Stripe Checkout is a full-page redirect, so the paywall intent
 * (gate, variant, analytics params) is kept in sessionStorage and completed on return
 * (`?checkout=success|cancel`). Exit-offer "once per session" also lives in sessionStorage,
 * which survives the same-tab round trip to Stripe.
 */
import type { PremiumGate } from '@/domain';
import { api, ApiError, type Offer, type Plan } from '@/services/api';

export type AnalyticsParams = Record<string, string | number | boolean | undefined>;
export interface CheckoutIntent {
  gate?: PremiumGate;
  variant: string;
  plan: Plan;
  offer?: Offer;
  params: AnalyticsParams;
  returnPath: string;
  at: number;
}

const INTENT_KEY = 'detectea.paywall.checkoutIntent';
const EXIT_SHOWN_KEY = 'detectea.paywall.exitOfferShown';
const RETENTION_PROMPT_KEY = 'detectea.paywall.retentionPromptShown';
const INTENT_TTL_MS = 2 * 60 * 60 * 1000;

function store(): Storage | undefined { try { return window.sessionStorage; } catch { return undefined; } }
function get(key: string): string | null { try { return store()?.getItem(key) ?? null; } catch { return null; } }
function set(key: string, value: string | undefined) { try { if (value === undefined) store()?.removeItem(key); else store()?.setItem(key, value); } catch { /* private mode */ } }

export function saveIntent(intent: CheckoutIntent): void { set(INTENT_KEY, JSON.stringify(intent)); }
export function readIntent(now = Date.now()): CheckoutIntent | undefined {
  const raw = get(INTENT_KEY);
  if (!raw) return undefined;
  try { const v = JSON.parse(raw) as CheckoutIntent; return now - v.at < INTENT_TTL_MS ? v : undefined; } catch { return undefined; }
}
export function clearIntent(): void { set(INTENT_KEY, undefined); }

export function exitOfferShownThisSession(): boolean { return get(EXIT_SHOWN_KEY) === '1'; }
export function markExitOfferShown(): void { set(EXIT_SHOWN_KEY, '1'); }
export function retentionPromptShownThisSession(): boolean { return get(RETENTION_PROMPT_KEY) === '1'; }
export function markRetentionPromptShown(): void { set(RETENTION_PROMPT_KEY, '1'); }

export type CheckoutOutcome = 'success' | 'cancel';
/** Reads `checkout=` from a search string and returns the cleaned search (checkout + session_id removed). */
export function parseCheckoutReturn(search: string): { outcome?: CheckoutOutcome; cleanedSearch: string } {
  const params = new URLSearchParams(search);
  const raw = params.get('checkout');
  const outcome = raw === 'success' || raw === 'cancel' ? raw : undefined;
  params.delete('checkout');
  params.delete('session_id');
  const rest = params.toString();
  return { outcome, cleanedSearch: rest ? `?${rest}` : '' };
}

export function currentReturnPath(): string {
  const { cleanedSearch } = parseCheckoutReturn(window.location.search);
  return `${window.location.pathname}${cleanedSearch}`;
}

/**
 * Creates the Checkout Session, stores the intent and leaves for Stripe.
 * Resolves 'already_premium' when the Worker refuses because the entitlement is active.
 */
export async function startCheckout(input: { plan: Plan; offer?: Offer; locale: string; gate?: PremiumGate; variant: string; params: AnalyticsParams }): Promise<'redirected' | 'already_premium'> {
  const returnPath = currentReturnPath();
  try {
    const { url } = await api.checkout({ plan: input.plan, offer: input.offer, locale: input.locale, returnPath });
    saveIntent({ gate: input.gate, variant: input.variant, plan: input.plan, offer: input.offer, params: input.params, returnPath, at: Date.now() });
    window.location.assign(url);
    return 'redirected';
  } catch (error) {
    if (error instanceof ApiError && error.status === 409 && error.message.includes('already_premium')) return 'already_premium';
    throw error;
  }
}
